import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Link, useParams } from 'react-router-dom';
import StatisticsAPI from '../../axios/statistics';
import { ConfigContext } from '../../contexts/config-context';
import { formatNumber } from '../main-page/home-utils';
import { listingCover } from '../search-page/components/result-card';
import { PanelIcon } from '../user-panel/user-panel';
import "./statistics.css";
import { ICON_PATHS } from '../../components/shared-components/icon-paths';

const RANGES = [7, 30];
// every n-th day gets a date under the chart, so the labels never run into each other
const LABEL_EVERY = { 7: 1, 30: 5 };
const Y_TICKS = 4;

const icon = (path) => () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{path}</svg>
);
const EyeIcon = icon(<path d={ICON_PATHS.eye} />);
const HeartIcon = icon(<path d="M12 20.5s-7.5-4.6-9.3-9.3C1.4 7.8 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.3 4.3 6.7-1.8 4.7-9.3 9.3-9.3 9.3Z" />);
const TrendIcon = icon(<path d="M4 19h16M5 15l4-4 3 3 6-6" />);
const StarIcon = icon(<path d="m12 3.5 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9Z" />);
const InfoIcon = icon(<><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>);

// the dates come as YYYY-MM-DD, read as local days so they never shift by the time zone
const parseDay = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};
const shortDate = (s) => {
    const d = parseDay(s);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
};

// a round step, so the axis reads 0, 10, 20... rather than 0, 9.5, 19...
const niceMax = (max) => {
    if (max <= Y_TICKS) return Y_TICKS;
    const raw = max / Y_TICKS;
    const magnitude = Math.pow(10, Math.floor(Math.log10(raw)));
    const step = [1, 2, 2.5, 5, 10].map(f => f * magnitude).find(s => s >= raw);
    return step * Y_TICKS;
};

const StatTile = ({ label, value, note, noteClass = '', Icon, tone }) => (
    <div className='hs-stat-tile'>
        <div className='hs-stat-tile-head'>
            <span>{label}</span>
            <span className={`hs-stat-tile-icon ${tone}`}><Icon /></span>
        </div>
        <strong>{value}</strong>
        <span className={`hs-stat-tile-note ${noteClass}`}>{note}</span>
    </div>
);

const DayChart = ({ days, unit }) => {
    const { t } = useTranslation(["profile"]);
    const [hover, setHover] = useState(null);
    const max = niceMax(Math.max(...days.map(d => d.count)));
    const ticks = Array.from({ length: Y_TICKS + 1 }, (_, i) => (max / Y_TICKS) * (Y_TICKS - i));
    const every = LABEL_EVERY[days.length] || 1;
    // the labels count back from today, so the newest day always has its date
    const labelled = (i) => (days.length - 1 - i) % every === 0;

    return (
        <div className='hs-stat-chart' role='img' aria-label={t('stats_chart_label')}>
            <div className='hs-stat-chart-y'>
                {ticks.map(v => <span key={v}>{formatNumber(v)}</span>)}
            </div>
            <div className='hs-stat-chart-plot'>
                <div className='hs-stat-chart-grid'>
                    {ticks.map(v => <span key={v}></span>)}
                </div>
                <div className='hs-stat-chart-bars'>
                    {days.map((d, i) => {
                        const height = (d.count / max) * 100;
                        return (
                        <div key={d.date} className={`hs-stat-chart-col ${hover === i ? 'on' : ''}`}
                            onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                            <span className='hs-stat-chart-bar' style={{ height: `${height}%` }}></span>
                            {/* just above the bar, on the side with more room */}
                            {hover === i &&
                                <span className={`hs-stat-chart-tip ${i > days.length / 2 ? 'left' : ''}`} style={{ bottom: `calc(${Math.min(height, 85)}% + 8px)` }}>
                                    <b>{formatNumber(d.count)}</b> {t(unit)}
                                    <em>{shortDate(d.date)}</em>
                                </span>}
                        </div>
                        );
                    })}
                </div>
                <div className='hs-stat-chart-x'>
                    {days.map((d, i) => <span key={d.date}>{labelled(i) ? shortDate(d.date) : ''}</span>)}
                </div>
            </div>
        </div>
    );
};

export const Statistics = () => {
    const { id } = useParams();
    const { api } = useContext(ConfigContext);
    const { t, i18n } = useTranslation(["profile", "listing"]);
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);
    const [series, setSeries] = useState('views');
    const [range, setRange] = useState(30);
    const lang = i18n.language;

    useEffect(() => {
        let cancelled = false;
        setFailed(false);
        StatisticsAPI.getStatisticsData(id, lang)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [id, lang]);

    if (failed) return <div className='hs-panel-card hs-stat-empty'>{t('stats_error')}</div>;
    if (!data) return null;

    const { listing, views, follows, followers } = data;
    const totalViews = views.reduce((sum, d) => sum + d.count, 0);
    const newFollows = follows.reduce((sum, d) => sum + d.count, 0);
    const average = (totalViews / views.length).toLocaleString(lang === 'sr' ? 'sr-RS' : 'en-GB', { maximumFractionDigits: 1 });
    // the latest of the busiest days
    const top = views.reduce((best, d) => d.count >= best.count ? d : best, views[0]);
    const shown = (series === 'views' ? views : follows).slice(-range);

    const expired = new Date(listing.date_active) < new Date();
    const status = !listing.is_active ? { label: t('status_inactive'), className: 'gray' }
        : expired ? { label: t('status_expired'), className: 'gray' }
            : { label: t('status_active'), className: 'green' };
    const viewable = listing.is_active && !expired;
    const price = parseFloat(listing.price) || 0;
    const hasPhoto = listing.photos?.length > 0;

    return (
        <div className='hs hs-stat'>
            <div className='hs-panel-card hs-stat-listing'>
                <div className={`hs-stat-listing-photo ${hasPhoto ? '' : 'empty'}`}
                    style={hasPhoto ? { backgroundImage: `url(${listingCover(listing, api)})` } : undefined}>
                    {!hasPhoto && <PanelIcon name='home' size={24} />}
                </div>
                <div className='hs-stat-listing-info'>
                    <span className='hs-stat-listing-title'>
                        <span>{listing.title}</span>
                        <span className={`hs-panel-badge small ${status.className}`}>{status.label}</span>
                    </span>
                    <span className='hs-stat-listing-meta'>
                        {[listing.place?.label, listing.subplace?.label, listing.location?.label,
                            price > 0 ? `${formatNumber(price)} €` : t('price_placeholder', { ns: 'listing' })].filter(Boolean).join(' · ')}
                    </span>
                </div>
                <div className='hs-stat-listing-actions'>
                    <Link to={listing.is_active ? '/korisnik/oglasi' : '/korisnik/neaktivni-oglasi'} className='hs-panel-outline'>
                        <span aria-hidden='true'>‹</span>{t('my_listings')}
                    </Link>
                    {viewable && <Link to={`/oglas/${listing.id}`} className='hs-panel-soft'>{t('view_listing')}</Link>}
                </div>
            </div>

            <div className='hs-stat-tiles'>
                <StatTile label={t('stats_total_views')} value={formatNumber(totalViews)} note={t('stats_last_30')} Icon={EyeIcon} tone='blue' />
                <StatTile label={t('stats_followers')} value={formatNumber(followers)} Icon={HeartIcon} tone='orange'
                    note={newFollows > 0 ? t('stats_new_follows', { n: formatNumber(newFollows) }) : t('stats_no_new_follows')}
                    noteClass={newFollows > 0 ? 'up' : ''} />
                <StatTile label={t('stats_daily_average')} value={average} note={t('stats_views_per_day')} Icon={TrendIcon} tone='gray' />
                <StatTile label={t('stats_top_day')} value={top.count > 0 ? shortDate(top.date) : '–'} Icon={StarIcon} tone='gray'
                    note={top.count > 0 ? t('stats_views_n', { n: formatNumber(top.count) }) : t('stats_no_views')} />
            </div>

            <div className='hs-panel-card hs-stat-chart-card'>
                <div className='hs-stat-chart-head'>
                    <div>
                        <h2>{t(series === 'views' ? 'stats_views_by_day' : 'stats_follows_by_day')}</h2>
                        <p>{shortDate(shown[0].date)} – {shortDate(shown[shown.length - 1].date)}{parseDay(shown[shown.length - 1].date).getFullYear()}.</p>
                    </div>
                    <div className='hs-stat-chart-tools'>
                        <div className='hs-stat-seg'>
                            {['views', 'follows'].map(s => (
                                <button key={s} className={series === s ? 'on' : ''} onClick={() => setSeries(s)}>{t(`stats_${s}`)}</button>
                            ))}
                        </div>
                        <div className='hs-stat-seg'>
                            {RANGES.map(r => (
                                <button key={r} className={range === r ? 'on' : ''} onClick={() => setRange(r)}>{t('stats_days', { n: r })}</button>
                            ))}
                        </div>
                    </div>
                </div>
                <DayChart days={shown} unit={series === 'views' ? 'stats_unit_views' : 'stats_unit_follows'} />
            </div>

            {!listing.promoted &&
                <div className='hs-stat-tip'>
                    <InfoIcon />
                    <p>{t('stats_tip_1')}<Link to='/korisnik/oglasavanje'>{t('stats_tip_link')}</Link>{t('stats_tip_2')}</p>
                </div>}
        </div>
    );
};

export default Statistics;
